<template>
	<div class="view-content">
		<div class="page-heading">
			<h1>Monetization by Ad Network</h1>
		</div>
		<query-form v-model:query="query" :loading="loading" @apply="getData(true)">
			<template #display>
				<div class="display-value">
					<div class="label">Date Range:</div>
					<div class="value">
						{{ formatDate(query.date_range, 'ddd, MMM DD, YYYY') }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Placement:</div>
					<div class="value">
						{{ displaySelected(query.filters.placement, placement_options) }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Vertical:</div>
					<div class="value">
						{{ displaySelected(query.filters.vertical_id, $root.appStore.verticalOptions) }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">State:</div>
					<div class="value">
						{{ displaySelected(query.filters.state, state_options) }}
					</div>
				</div>
			</template>
			<template #utils>
				<p-button
					v-tooltip.top="'Refresh'"
					icon="pi pi-refresh"
					aria-label="Refresh"
					class="mr-2"
					@click="getData(true)"
				/>
			</template>
			<template #form="form">
				<row>
					<column max-width="350px">
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Date Range:</label>
								<div class="controls">
									<div class="field">
										<date-range-picker :max-date_range="62" v-model="form.query.date_range" />
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Placement:</label>
								<div class="controls">
									<div class="field">
										<p-dropdown
											v-model="form.query.filters.placement"
											:options="placement_options"
											option-label="label"
											option-value="value"
											placeholder="Select Placement"
										/>
									</div>
								</div>
							</div>
						</div>
					</column>
					<gutter size="40px" />
					<column max-width="350px">
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Vertical:</label>
								<div class="controls">
									<div class="field">
										<p-multi-select
											class="w-full"
											v-model="form.query.filters.vertical_id"
											:options="$root.appStore.verticalOptions"
											option-label="label"
											option-value="value"
											placeholder="Select Vertical"
											display-selected-label
											show-clear
											filter
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">States:</label>
								<div class="controls">
									<div class="field">
										<p-multi-select
											class="w-full"
											v-model="form.query.filters.state"
											filter
											filter-placeholder="Filter States"
											placeholder="Select State"
											:options="state_options"
											option-label="label"
											option-value="value"
										/>
									</div>
								</div>
							</div>
						</div>
					</column>
					<gutter class="hide-mobile" size="40px" />
					<column class="hide-mobile"></column>
				</row>
			</template>
		</query-form>
		<!-- <div class="card padded text-center">
			<h2>Ad Monetization vs Total Monetization</h2>
			<p-chart
				ref="ad_monetization_chart"
				type="bar"
				:data="monetizationChartData"
				:options="chartOptions(true)"
				class="max-h-25rem w-full"
				style="min-height: 12rem; height: 100%; display: flex; justify-content: center; align-items: center"
			/>
		</div> -->
		<gutter size="20px" />
		<div class="card padded">
			<!-- Shows sum of monetization data for each ad network for a date range -->
			<p-data-table ref="ad-network-monetization" :loading="loading" :value="monetization_data">
				<template #loading>
					<line-loader :show="loading" />
				</template>
				<template #empty> <div class="dim">No Data</div> </template>
				<p-column header="Ad Network" sortable field="network_id">
					<template #body="row">
						{{ row.data.network_id }}
					</template>
				</p-column>
				<p-column header="Leads" sortable field="leads">
					<template #body="row">
						{{ row.data.leads }}
					</template>
				</p-column>
				<p-column header="Impressions" sortable field="total_impressions">
					<template #body="row">
						{{ row.data.total_impressions }}
					</template>
				</p-column>
				<p-column header="Share of Impressions" sortable field="share">
					<template #body="row">
						{{ percentage(row.data.share/100, 2) }}
					</template>
				</p-column>
				<p-column sortable field="ctr">
					<template #header>
						<span v-tooltip.top="'Clickthrough Rate (Impressions/Clicks)'">CTR</span>
					</template>
					<template #body="row">
						{{ percentage(row.data.ctr, 2) }}
					</template>
				</p-column>
				<p-column header="Expected Monetization" sortable field="expected_monetization">
					<template #body="row">
						{{ currency(row.data.expected_revenue, 0) }}
					</template>
				</p-column>
				<p-column header="Ad Monetization" sortable field="ad_monetization">
					<template #body="row">
						{{ currency(row.data.ad_monetization, 0) }}
					</template>
				</p-column>
				<p-column sortable field="total_monetization">
					<template #header>
						<span v-tooltip.top="'Call modal also shown'">Total Monetization</span>
					</template>
					<template #body="row">
						{{ currency(row.data.total_monetization, 0) }}
					</template>
				</p-column>
				<p-column sortable field="rpi">
					<template #header>
						<span v-tooltip.top="'Revenue/Impression'">RPI</span>
					</template>
					<template #body="row">
						{{ currency(row.data.rpi, 2) }}
					</template>
				</p-column>
				<p-column sortable field="rpc">
					<template #header>
						<span v-tooltip.top="'Revenue/Click'">RPC</span>
					</template>
					<template #body="row">
						{{ currency(row.data.rpc, 2) }}
					</template>
				</p-column>
			</p-data-table>
		</div>
	</div>
</template>

<script lang="ts">
import { currency, formatDate, title, percentage, displaySelected } from '@/lib/Filters';
import { getAdAuctionPlacementsAsOptions, reportAdAuctionMonetization } from '@GQL';
import STATES from '@/lib/Data/states.json';
import queryForm from '@/components/widgets/QueryForm.vue';
import tableTools from '@/components/widgets/TableTools.vue';
import pChart from 'primevue/chart';
import { merge } from 'lodash-es';
import dateRangePicker from '@/components/forms/DateRangePicker.vue';
import dayjs from 'dayjs';

export default {
	name: 'ad-network-monetization',
	components: {
		dateRangePicker,
		queryForm,
		tableTools,
		pChart,
	},
	inject: ['mq'],
	data() {
		return {
			loading: false,
			placement_options: [],
			state_options: STATES,
			query: {
				date_range: [dayjs().subtract(1, 'week').toDate(), dayjs().endOf('day').toDate()],
				filters: {
					placement: [],
					vertical_id: [],
					state: [],
				},
			},
			total_rows: 0,
			monetization_data: [],
			totals: {},
			leads: {},
			impressions: {},
			ad_monetization: {},
		};
	},
	methods: {
		currency,
		formatDate,
		title,
		percentage,
		displaySelected,
		async getData(refresh = false) {
			this.loading = true;
			const params = this.query;
			this.monetization_data = await reportAdAuctionMonetization(params, refresh);
			this.loading = false;
		},
		chartOptions(is_currency = false) {
			const options = {
				responsive: true,
				plugins: {
					legend: {
						position: 'bottom',
					},
				},
				x: {
					stacked: true,
				},
				y: {
					stacked: true,
				},
			};

			if (is_currency) {
				merge(options, {
					plugins: {
						tooltip: {
							callbacks: {
								label(context) {
									return `${context.dataset.label}: ${currency(context.dataset.data[context.dataIndex])}`;
								},
							},
						},
					},
					scales: {
						y: {
							ticks: {
								callback(value, index) {
									return currency(value);
								},
							},
						},
					},
					layout: {
						padding: 10,
					},
				});
			}

			return options;
		},
	},
	computed: {
		monetizationChartData() {
			const documentStyle = getComputedStyle(document.documentElement);
			return {
				labels: this.monetization_data.map((row) => row.network_id),
				datasets: [
					{
						type: 'bar',
						label: 'Ad Monetization',
						data: this.monetization_data.map((row) => row.ad_monetization),
						backgroundColor: documentStyle.getPropertyValue('--green-500'),
						options: {
							plugins: {
								legend: false,
							},
						},
					},
					{
						type: 'bar',
						label: 'Other Monetization',
						data: this.monetization_data.map((row) => row.total_monetization - row.ad_monetization),
						backgroundColor: documentStyle.getPropertyValue('--green-200'),
						opacity: 0.5,
						options: {
							plugins: {
								legend: false,
							},
						},
					},
				],
			};
		},
	},
	async mounted() {
		this.placement_options = await getAdAuctionPlacementsAsOptions();
		await this.getData();
	},
};
</script>

<style lang="less" scoped>
@import (reference) '@/styles/responsive';
.chart-container {
	display: flex;
	justify-content: center;
	align-items: center;
	max-width: 100%;
	height: 100%;
}
</style>
